/**
 * Row / more-menu actions on documents (card 3.4): Duplicate, Move to…, Copy
 * link, Pin. Kept out of documentsStore.js (which is the MCP documents surface
 * and stays small) — this module may toast and touch the clipboard; the store
 * must not.
 *
 * All of it works on the records the store already writes; nothing here adds a
 * record type except through documentsPins.js (`docprefs`).
 */
import i18n from '../../../i18n';
import { upsertRecord } from '../../../records/recordsClient';
import { copyTextToClipboard } from '../../../utils/clipboard';
import { useNotificationStore } from '../../../store/notificationStore';
import { documentUri } from '../../../mcp/resources';
import { requireLocalCapacityFor } from '../../../plans/localGate';
import {
  countOwnDocuments,
  keyBetween,
  keysExhausted,
  listTree,
  moveNode,
  padKey,
  readDocument,
  readTreeRows,
  recordsContext,
  uid,
} from './documentsStore';
import { collectSubtree, indexNodes, isDescendant } from './documentsTree';
import { bucketScopeOf, dataOf, scopeFilterOf } from './documentsScopeTree';
import { isDocDeleted } from './deletedDocsGuard';
import { docBodyData } from './docBodyCodec';

export { listPins, togglePin, reorderPins, isPinned } from './documentsPins';

const EXTENSION_ID = 'documents';
const bySortKey = (a, b) => String(a.sortKey).localeCompare(String(b.sortKey)) || String(a.id).localeCompare(String(b.id));

/** Sort key for a new last child of `parentId` in `nodes`. */
function keyAtEnd(nodes, parentId) {
  const siblings = nodes.filter((n) => (n.parentId ?? null) === (parentId ?? null)).sort(bySortKey);
  return keyBetween(siblings.length ? siblings[siblings.length - 1].sortKey : null, null);
}

/** Every old id inside a body (page links to copied sub-pages) -> its copy's id. */
function remapIds(value, idMap) {
  if (value == null || idMap.size === 0) return value;
  let text = typeof value === 'string' ? value : JSON.stringify(value);
  for (const [from, to] of idMap) text = text.split(from).join(to);
  return typeof value === 'string' ? text : JSON.parse(text);
}

/** True when the document has sub-pages — the UI asks before a deep duplicate. */
export function hasSubpages(nodes, docId) {
  return (nodes || []).some((n) => String(n.parentId) === String(docId));
}

/**
 * Duplicates a document right after itself: new ids, title "… (copy)", same
 * parent, every other data key carried (a root keeps its workspace). With
 * `includeSubpages`, the whole subtree is copied too — children keep their
 * titles and order, and links inside the copied bodies that pointed at a copied
 * page point at its copy. Resolves `{ id, idMap }`.
 *
 * Writes go parent-first, each document before its body, so an interrupted
 * duplicate leaves a shorter copy, never an orphan.
 *
 * Known limit: inline images keep their original storage keys, which live under
 * the SOURCE document's prefix — see the signal 2026-10-13-docs-store.
 */
export async function duplicateDocument(docId, scope, { includeSubpages = false } = {}) {
  const id = String(docId);
  if (isDocDeleted(id)) throw new Error(`Document "${id}" was deleted.`);
  const bucket = bucketScopeOf(scope);
  const ctx = recordsContext(bucket);
  const [nodes, rows] = await Promise.all([listTree(bucket), readTreeRows(bucket)]);
  const index = indexNodes(nodes, bySortKey);
  const source = index.nodeById.get(id);
  if (!source || source.type !== 'document') throw new Error(`Document "${id}" not found.`);
  // Without the rows a copy would silently lose every data key but the title.
  if (!rows) throw new Error('Could not read the document tree.');

  const subtree = includeSubpages ? collectSubtree(index, id) : [source];
  // Plans v2: a copy adds every document in it (folders don't count).
  if (bucket?.type !== 'team') {
    const adding = subtree.filter((n) => n.type === 'document').length;
    if (!(await requireLocalCapacityFor('documents', countOwnDocuments, { adding }))) {
      throw Object.assign(new Error('plan_limit'), { planLimit: true });
    }
  }
  const idMap = new Map(subtree.map((n) => [String(n.id), uid()]));

  const siblings = nodes.filter((n) => (n.parentId ?? null) === (source.parentId ?? null)).sort(bySortKey);
  const next = siblings[siblings.findIndex((n) => String(n.id) === id) + 1];
  const rootKey = next && keysExhausted(source.sortKey, next.sortKey)
    ? keyAtEnd(nodes, source.parentId)
    : keyBetween(source.sortKey, next ? next.sortKey : null);

  for (const node of subtree) {
    const isRoot = String(node.id) === id;
    const row = rows.get(`${node.type}:${node.id}`);
    const data = row ? dataOf(row) : {};
    if (!isRoot) delete data.scopeId; // nested rows never carry one
    const label = node.name || '';
    if (node.type === 'folder') data.name = label;
    else if (isRoot) {
      data.title = label
        ? i18n.t('docStore.copyTitle', { title: label })
        : i18n.t('docStore.untitledCopy');
    } else data.title = label;
    const newId = idMap.get(String(node.id));
    // eslint-disable-next-line no-await-in-loop
    await upsertRecord({
      workspaceId: ctx.workspaceId,
      extensionId: EXTENSION_ID,
      record: {
        id: newId,
        record_type: node.type,
        parent_id: isRoot ? source.parentId ?? null : idMap.get(String(node.parentId)),
        sort_key: isRoot ? rootKey : node.sortKey,
        data,
      },
    });
    if (node.type !== 'document') continue;
    // eslint-disable-next-line no-await-in-loop
    const body = await readDocument(node.id, bucket);
    // eslint-disable-next-line no-await-in-loop
    await upsertRecord({
      workspaceId: ctx.workspaceId,
      extensionId: EXTENSION_ID,
      record: {
        id: `${newId}::body`,
        record_type: 'docbody',
        parent_id: newId,
        sort_key: padKey(0),
        data: docBodyData(
          newId,
          remapIds(body?.bodyHtml || '', idMap),
          remapIds(body?.bodyLexical ?? null, idMap)
        ),
      },
    });
  }
  return { id: idMap.get(id), idMap };
}

/**
 * Where "Move to…" may put `node` (the SearchableSelect's options, as data).
 * `nodes` is the tree the user is looking at (a workspace-filtered `listTree`),
 * `scopes` the user's workspaces (`listScopes`). Returns, in order:
 *
 * - `{ kind: 'root' }` — the top of the current tree (omitted when the node is
 *   already there);
 * - `{ kind: 'folder' | 'document', id, path }` for each folder (and, with
 *   `pages`, each page) that is not the node itself, one of its descendants, or
 *   its current parent; `path` is the ancestor names, for the option label;
 * - `{ kind: 'workspace', scopeId, name, icon, color }` for every OTHER
 *   personal workspace, and `{ kind: 'workspace', scopeId: null }` (Personal)
 *   when the current tree is a workspace. Only offered in the personal bucket —
 *   the device and team scopes have no sub-workspaces.
 */
export function moveTargets(node, nodes, { scope, scopes = [], pages = false } = {}) {
  const index = indexNodes(nodes, bySortKey);
  const out = [];
  if ((node.parentId ?? null) !== null) out.push({ kind: 'root' });
  const pathOf = (n) => {
    const names = [];
    const seen = new Set();
    let cur = n;
    while (cur && !seen.has(String(cur.id))) {
      seen.add(String(cur.id));
      names.unshift(cur.name || '');
      cur = cur.parentId == null ? null : index.nodeById.get(String(cur.parentId));
    }
    return names;
  };
  for (const n of [...nodes].sort(bySortKey)) {
    if (n.type !== 'folder' && !(pages && n.type === 'document')) continue;
    if (String(n.id) === String(node.id) || String(n.id) === String(node.parentId)) continue;
    if (isDescendant(index, n.id, node.id)) continue;
    out.push({ kind: n.type, id: n.id, path: pathOf(n) });
  }
  const filter = scopeFilterOf(scope);
  const personal = !scope?.type || scope.type === 'personal';
  if (personal) {
    if (filter) out.push({ kind: 'workspace', scopeId: null });
    for (const s of scopes) {
      if (s.id === filter) continue;
      out.push({ kind: 'workspace', scopeId: s.id, name: s.name, icon: s.icon, color: s.color });
    }
  }
  return out;
}

/**
 * Executes one `moveTargets` entry: a reparent inside the current tree (placed
 * last), or a move to the top of another personal workspace (`scopeId`
 * rewrite on the root — one record, its subtree follows). Moving between
 * buckets (personal ↔ team, cloud ↔ device) is not a Move to… target: that is
 * records/itemMove.js and the board-style share flows.
 */
export async function moveToTarget(node, target, scope, nodes) {
  if (target.kind === 'workspace') {
    const dest = { ...bucketScopeOf(scope), scopeId: target.scopeId ?? null };
    const roots = await listTree(dest);
    await moveNode(node, null, keyAtEnd(roots, null), dest);
    return dest;
  }
  const parentId = target.kind === 'root' ? null : target.id;
  if (parentId != null && (String(parentId) === String(node.id) || isDescendant(nodes, parentId, node.id))) {
    const err = new Error('A page cannot move into itself.');
    err.code = 'move_into_self';
    throw err;
  }
  await moveNode(node, parentId, keyAtEnd(nodes, parentId), scope);
  return scope;
}

/** `apiboo://documents/<wire scope>/<id>` — the deep link reveal.js handles. */
export function documentLink(docId, scope) {
  const wire = scope?.type === 'team' && scope.teamId != null ? `team:${scope.teamId}` : 'personal';
  return documentUri(wire, docId);
}

/** Copies the document's deep link and says so. Resolves the link, or null when the copy failed. */
export async function copyDocumentLink(docId, scope) {
  const link = documentLink(docId, scope);
  const ok = await copyTextToClipboard(link);
  const notes = useNotificationStore.getState();
  if (ok) notes.success(i18n.t('docStore.linkCopied'), i18n.t('docStore.linkCopiedTitle'));
  else notes.error(i18n.t('docStore.linkCopyFailed'), i18n.t('docStore.linkCopyFailedTitle'));
  return ok ? link : null;
}
